import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, CreditCard, Check, Shield, Loader2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

const monthlyAddons = [
  {
    id: 'starter',
    name: 'Starter Support',
    price: '£99',
    priceNum: 9900,
    period: '/month',
    description: 'Essential support to keep your site running smoothly',
    features: [
      'Hosting & SSL',
      'Monthly backups',
      'Security updates',
      'Content changes (2/month)',
      'Email support',
      'Uptime monitoring',
    ],
  },
  {
    id: 'professional',
    name: 'Professional Support',
    price: '£249',
    priceNum: 24900,
    period: '/month',
    description: 'Growth-focused support with more flexibility',
    features: [
      'Everything in Starter',
      'Priority hosting',
      'Weekly backups',
      'Content changes (10/month)',
      'Performance reports',
      'Minor design tweaks',
      'Phone & email support',
      'Quarterly review call',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise Support',
    price: '£499',
    priceNum: 49900,
    period: '/month',
    description: 'Full-service partnership for ambitious businesses',
    features: [
      'Everything in Professional',
      'Dedicated account manager',
      'Daily backups',
      'Unlimited content changes',
      'A/B testing setup',
      'Analytics dashboard',
      'New page builds (2/month)',
      '24/7 priority support',
    ],
  },
];

const Subscribe = () => {
  const [searchParams] = useSearchParams();
  const planName = searchParams.get('planName') || 'Standard Website Build';
  const customerName = searchParams.get('name') || '';
  const customerEmail = searchParams.get('email') || '';
  const customerCompany = searchParams.get('company') || '';
  const customerPhone = searchParams.get('phone') || '';
  const customerMessage = searchParams.get('message') || '';

  const [selectedAddon, setSelectedAddon] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubscribe = async () => {
    if (!selectedAddon) return;
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planId: selectedAddon,
          name: customerName,
          email: customerEmail,
          company: customerCompany,
          phone: customerPhone,
          message: customerMessage,
        }),
      });

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-dark pt-24 pb-12">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
              <Check size={16} className="text-white" />
            </div>
            <span className="text-green-600 font-medium">Website Build Paid</span>
          </div>

          <h1 className="text-3xl font-bold text-primary dark:text-white mb-4">
            One More Step
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-lg mb-8">
            Great! Your <strong>{planName}</strong> payment is confirmed. 
            Add a monthly support plan to keep your site running smoothly, or skip this step.
          </p>

          {/* Build Confirmation */}
          <div className="bg-green-50 dark:bg-green-900/20 rounded-2xl p-6 mb-8 border border-green-200 dark:border-green-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center">
                  <Check size={20} className="text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-primary dark:text-white">{planName}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Paid ✓</p>
                </div>
              </div>
              <span className="text-green-600 font-medium">Confirmed</span>
            </div>
          </div>

          {/* Monthly Options */}
          <h2 className="text-2xl font-bold text-primary dark:text-white mb-6">
            Add Monthly Support
          </h2>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {monthlyAddons.map((addon) => (
              <motion.div
                key={addon.id}
                whileHover={{ scale: 1.02 }}
                onClick={() => setSelectedAddon(selectedAddon === addon.id ? null : addon.id)}
                className={`cursor-pointer rounded-2xl p-6 border-2 transition-all ${
                  selectedAddon === addon.id
                    ? 'border-highlight bg-highlight/5 dark:bg-highlight/10'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-primary dark:text-white">{addon.name}</h3>
                  {selectedAddon === addon.id && (
                    <div className="w-6 h-6 bg-highlight rounded-full flex items-center justify-center">
                      <Check size={14} className="text-white" />
                    </div>
                  )}
                </div>
                <div className="mb-4">
                  <span className="text-2xl font-bold text-primary dark:text-white">{addon.price}</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">{addon.period}</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{addon.description}</p>
                <ul className="space-y-2">
                  {addon.features.slice(0, 4).map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <Check size={14} className="text-green-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="/"
              className="flex-1 py-4 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-all text-center flex items-center justify-center gap-2"
            >
              <ArrowLeft size={18} />
              Skip — I'm All Set
            </a>
            <button
              onClick={handleSubscribe}
              disabled={!selectedAddon || isSubmitting}
              className="flex-1 py-4 bg-primary text-white font-semibold rounded-xl hover:bg-secondary transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard size={18} />
                  Subscribe Now
                </>
              )}
            </button>
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 justify-center mt-6">
            <Shield size={16} />
            <span>Cancel anytime. No long-term commitment.</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Subscribe;
